import {
  createContext,
  useContext,
  useState,
  useCallback,
  type ReactNode,
} from 'react';

interface SidebarContextType {
  expandedItems: string[];
  hoveredItem: string | null;
  toggleExpanded: (itemId: string) => void;
  setHoveredItem: (itemId: string | null) => void;
  isExpanded: (itemId: string) => boolean;
}

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

export function SidebarProvider({ children }: { children: ReactNode }) {
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);

  const toggleExpanded = useCallback((itemId: string) => {
    setExpandedItems((prev) =>
      prev.includes(itemId)
        ? prev.filter((id) => id !== itemId)
        : [...prev, itemId]
    );
  }, []);

  const isExpanded = useCallback(
    (itemId: string) => expandedItems.includes(itemId),
    [expandedItems]
  );

  return (
    <SidebarContext.Provider
      value={{
        expandedItems,
        hoveredItem,
        toggleExpanded,
        setHoveredItem,
        isExpanded,
      }}
    >
      {children}
    </SidebarContext.Provider>
  );
}

export function useSidebarContext() {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error('useSidebarContext must be used within SidebarProvider');
  }
  return context;
}
